import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight, FiCheckCircle, FiMail, FiZap } from 'react-icons/fi';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import '../home/Home.css';
import './ResetPassword.css';

const expApiBaseUrl = getApiBaseUrl();

function ResetPassword() {
	const { token: expUrlToken } = useParams();
	const expHasToken = useMemo(() => Boolean(expUrlToken && String(expUrlToken).trim()), [expUrlToken]);

	const [expEmail, setExpEmail] = useState('');
	const [expNewPassword, setExpNewPassword] = useState('');
	const [expConfirmPassword, setExpConfirmPassword] = useState('');
	const [expIsLoading, setExpIsLoading] = useState(false);
	const [expIsValidatingToken, setExpIsValidatingToken] = useState(false);
	const [expIsTokenValid, setExpIsTokenValid] = useState(false);
	const [expTokenEmail, setExpTokenEmail] = useState('');
	const [expErrorMessage, setExpErrorMessage] = useState('');
	const [expSuccessMessage, setExpSuccessMessage] = useState('');

	useEffect(() => {
		const expValidateToken = async () => {
			if (!expHasToken) {
				setExpIsTokenValid(false);
				setExpTokenEmail('');
				return;
			}

			setExpErrorMessage('');
			setExpIsValidatingToken(true);
			try {
				const expResponse = await fetch(`${expApiBaseUrl}/api/users/reset-password/validate/${expUrlToken}`);
				const expContentType = expResponse.headers.get('content-type') || '';
				const expData = expContentType.includes('application/json')
					? await expResponse.json()
					: { success: false, message: 'Réponse invalide du serveur.' };

				if (!expResponse.ok || !expData.success) {
					setExpIsTokenValid(false);
					setExpErrorMessage(expData.message || 'Lien invalide ou expiré.');
					return;
				}

				setExpIsTokenValid(true);
				setExpTokenEmail(expData.email || '');
			} catch {
				setExpIsTokenValid(false);
				setExpErrorMessage('Impossible de valider ce lien pour le moment.');
			} finally {
				setExpIsValidatingToken(false);
			}
		};

		expValidateToken();
	}, [expHasToken, expUrlToken]);

	const expHandleSubmit = async (expEvent) => {
		expEvent.preventDefault();
		setExpErrorMessage('');
		setExpSuccessMessage('');

		if (expHasToken) {
			if (!expNewPassword.trim() || !expConfirmPassword.trim()) {
				setExpErrorMessage('Veuillez remplir les deux champs mot de passe.');
				return;
			}

			if (expNewPassword.length < 8) {
				setExpErrorMessage('Le mot de passe doit contenir au moins 8 caractères.');
				return;
			}

			if (expNewPassword !== expConfirmPassword) {
				setExpErrorMessage('Les mots de passe ne correspondent pas.');
				return;
			}

			setExpIsLoading(true);
			try {
				const expResponse = await fetch(`${expApiBaseUrl}/api/users/reset-password`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						token: expUrlToken,
						newPassword: expNewPassword,
						confirmPassword: expConfirmPassword,
					}),
				});

				const expContentType = expResponse.headers.get('content-type') || '';
				const expData = expContentType.includes('application/json')
					? await expResponse.json()
					: { success: false, message: 'Réponse invalide du serveur.' };

				if (!expResponse.ok || !expData.success) {
					setExpErrorMessage(expData.message || 'Impossible de réinitialiser le mot de passe.');
					return;
				}

				setExpSuccessMessage(expData.message || 'Mot de passe réinitialisé avec succès.');
				setExpNewPassword('');
				setExpConfirmPassword('');
			} catch {
				setExpErrorMessage('Impossible de contacter le serveur. Vérifiez votre connexion.');
			} finally {
				setExpIsLoading(false);
			}

			return;
		}

		if (!expEmail.trim()) {
			setExpErrorMessage('Veuillez entrer votre adresse email.');
			return;
		}

		setExpIsLoading(true);
		try {
			const expResponse = await fetch(`${expApiBaseUrl}/api/users/forgot-password`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email: expEmail }),
			});

			const expContentType = expResponse.headers.get('content-type') || '';
			const expData = expContentType.includes('application/json')
				? await expResponse.json()
				: { success: false, message: 'Réponse invalide du serveur.' };

			if (!expResponse.ok || !expData.success) {
				setExpErrorMessage(expData.message || 'Impossible d\'envoyer le lien de réinitialisation.');
				return;
			}

			setExpSuccessMessage(expData.message || 'Un lien de réinitialisation a été envoyé à votre adresse email.');
			setExpEmail('');
		} catch {
			setExpErrorMessage('Impossible de contacter le serveur. Vérifiez votre connexion.');
		} finally {
			setExpIsLoading(false);
		}
	};

	return (
		<div className="vx-page rp-page">

			{/* ── NAVBAR ── */}
			<header className="vx-nav-wrap">
				<div className="vx-shell">
					<nav className="vx-nav">
						<Link to="/" className="vx-brand">
							<div className="vx-brand-mark">
								<img src="/assets/images/logo.png" alt="UltimateXDrive" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
							</div>
							<span className="vx-brand-name">UltimateXDrive</span>
						</Link>
						<div />
						<div className="vx-nav-cta">
							<Link to="/login" className="vx-btn vx-btn-ghost">
								<FiArrowLeft size={14} aria-hidden="true" />
								Connexion
							</Link>
						</div>
					</nav>
				</div>
			</header>

			{/* ── MAIN SPLIT ── */}
			<main className="rp-main">

				{/* LEFT — dark panel */}
				<div className="rp-left">
					<div className="rp-left-inner">
						<span className="rp-kicker">
							<FiZap size={13} aria-hidden="true" />
							Sécurité &amp; accès
						</span>
						<h1 className="rp-left-title">
							{expHasToken ? 'Choisissez' : 'Réinitialisez'}<br />votre mot de <em>passe</em>
						</h1>
						<p className="rp-left-sub">
							{expHasToken
								? 'Créez un nouveau mot de passe sécurisé pour protéger votre compte.'
								: 'Un lien sécurisé à usage unique vous sera envoyé. Suivez les instructions pour créer un nouveau mot de passe en toute sérénité.'}
						</p>
						<div className="rp-info-list">
							<div className="rp-info-item">
								<FiCheckCircle size={14} aria-hidden="true" />
								<span>{expHasToken ? 'Votre lien est vérifié avant changement' : 'Email sécurisé envoyé en quelques secondes'}</span>
							</div>
							<div className="rp-info-item">
								<FiCheckCircle size={14} aria-hidden="true" />
								<span>{expHasToken ? 'Nouveau mot de passe appliqué immédiatement' : 'Lien à usage unique — expire après utilisation'}</span>
							</div>
							<div className="rp-info-item">
								<FiCheckCircle size={14} aria-hidden="true" />
								<span>{expHasToken ? 'Connectez-vous ensuite avec vos nouveaux identifiants' : 'Vérifiez vos spams si l\'email n\'arrive pas'}</span>
							</div>
						</div>
					</div>
				</div>

				{/* RIGHT — form */}
				<div className="rp-right">
					{expHasToken && expIsValidatingToken ? (
						<div className="rp-success">
							<div className="rp-success-icon">
								<FiCheckCircle size={28} aria-hidden="true" />
							</div>
							<span className="vx-section-kicker">Validation en cours</span>
							<h2 className="rp-success-title">Vérification du lien</h2>
							<p className="rp-success-text">Veuillez patienter quelques secondes...</p>
						</div>
					) : null}

					{expHasToken && !expIsValidatingToken && !expIsTokenValid ? (
						<div className="rp-form">
							<div className="rp-form-head">
								<span className="vx-section-kicker">Lien invalide</span>
								<h2 className="rp-form-title">Ce lien ne fonctionne plus</h2>
								<p className="rp-form-sub">Demandez un nouveau lien de réinitialisation.</p>
							</div>
							{expErrorMessage ? <p className="rp-error" role="alert">{expErrorMessage}</p> : null}
							<Link to="/reset-password" className="vx-btn vx-btn-solid rp-btn-full">
								Recevoir un nouveau lien
								<FiArrowRight size={14} aria-hidden="true" />
							</Link>
						</div>
					) : null}

					{!expIsValidatingToken && (expSuccessMessage ? (
						<div className="rp-success">
							<div className="rp-success-icon">
								<FiCheckCircle size={28} aria-hidden="true" />
							</div>
							<span className="vx-section-kicker">{expHasToken ? 'Terminé' : 'Email envoyé'}</span>
							<h2 className="rp-success-title">{expHasToken ? 'Mot de passe mis à jour' : 'Vérifiez votre boîte mail'}</h2>
							<p className="rp-success-text">{expSuccessMessage}</p>
							<Link to="/login" className="vx-btn vx-btn-solid rp-btn-full">
								Se connecter
								<FiArrowRight size={14} aria-hidden="true" />
							</Link>
						</div>
					) : (!expHasToken || expIsTokenValid) ? (
						<form className="rp-form" onSubmit={expHandleSubmit} noValidate>
							<div className="rp-form-head">
								<span className="vx-section-kicker">{expHasToken ? 'Nouveau mot de passe' : 'Réinitialisation'}</span>
								<h2 className="rp-form-title">{expHasToken ? 'Définissez votre nouveau mot de passe' : 'Entrez votre email'}</h2>
								<p className="rp-form-sub">
									{expHasToken
										? `Compte concerné${expTokenEmail ? ` : ${expTokenEmail}` : ''}`
										: 'Nous vous enverrons un lien pour créer un nouveau mot de passe.'}
								</p>
							</div>

							{expHasToken ? (
								<>
									<div className="rp-field">
										<label htmlFor="rp-new-password" className="rp-label">Nouveau mot de passe</label>
										<input
											id="rp-new-password"
											type="password"
											className="rp-input"
											placeholder="Minimum 8 caractères"
											value={expNewPassword}
											onChange={(e) => setExpNewPassword(e.target.value)}
											autoComplete="new-password"
											required
										/>
									</div>
									<div className="rp-field">
										<label htmlFor="rp-confirm-password" className="rp-label">Confirmer le mot de passe</label>
										<input
											id="rp-confirm-password"
											type="password"
											className="rp-input"
											placeholder="Retapez le mot de passe"
											value={expConfirmPassword}
											onChange={(e) => setExpConfirmPassword(e.target.value)}
											autoComplete="new-password"
											required
										/>
									</div>
								</>
							) : (
								<div className="rp-field">
									<label htmlFor="rp-email" className="rp-label">
										<FiMail size={13} aria-hidden="true" />
										Adresse email
									</label>
									<input
										id="rp-email"
										type="email"
										className="rp-input"
										placeholder="exemple@ultimatexdrive.com"
										value={expEmail}
										onChange={(e) => setExpEmail(e.target.value)}
										autoComplete="email"
										required
									/>
								</div>
							)}

							{expErrorMessage ? (
								<p className="rp-error" role="alert">{expErrorMessage}</p>
							) : null}

							<button type="submit" className="vx-btn vx-btn-solid rp-btn-full" disabled={expIsLoading}>
								{expIsLoading ? (
									<span className="rp-loading">
										<span className="rp-loading-dot" />
										<span className="rp-loading-dot" />
										<span className="rp-loading-dot" />
										{expHasToken ? 'Mise à jour en cours...' : 'Envoi en cours...'}
									</span>
								) : (
									<>
										{expHasToken ? 'Mettre à jour mon mot de passe' : 'Envoyer le lien de réinitialisation'}
										<FiArrowRight size={14} aria-hidden="true" />
									</>
								)}
							</button>

							<div className="rp-form-footer">
								<Link to="/login" className="rp-back-link">
									<FiArrowLeft size={13} aria-hidden="true" />
									Se connecter
								</Link>
							</div>
						</form>
					) : null)}
				</div>

			</main>
		</div>
	);
}

export default ResetPassword;
